import React from 'react';
import { Link } from 'react-router';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import pluralize from 'pluralize';
import moment from 'moment';
import { usePopulate } from 'src/api/APIHooks';
import { useFeatureFlag } from 'common/hooks';
import { DataCell, DataRow } from 'src/common/tables/V2';
import { DollarAmount } from 'src/common/display/Money';
import ServiceAuthorizationStatusIndicator from './ServiceAuthorizationStatusIndicator';

const ServiceAuthorizationTableRow = ({
  authorizationRequest,
  showTimeInQueue,
}) => {
  const useSparseFieldsForPeopleFF = useFeatureFlag('PAYS-7267-include-internal-nonconsent');
  const showAssignee = useFeatureFlag('pays-8984-auth-assign-in-review');
  const lastUpdated = new Date(authorizationRequest.updated_at);

  usePopulate('requester', 'employee', authorizationRequest, {
    queryConfig: { placeholderData: undefined },
  });
  usePopulate('fee_schedule_program', 'fee_schedule_program', authorizationRequest, {
    queryConfig: { placeholderData: undefined },
  });
  usePopulate('person', 'person', authorizationRequest, {
    queryConfig: { enabled: !useSparseFieldsForPeopleFF, placeholderData: undefined },
  });
  usePopulate('assignee', 'employee', authorizationRequest, {
    queryConfig: { enabled: showAssignee, placeholderData: undefined },
  });

  const centsAmount = authorizationRequest.approved_cents ||
    authorizationRequest.requested_cents;
  const unitAmount = authorizationRequest.approved_unit_amount ||
    authorizationRequest.requested_unit_amount;

  return (
    <DataRow
      key={authorizationRequest.id}
      dataTestId="workqueue-table-row"
    >
      <DataCell className="text-action-blue">
        <Link to={`/authorizations/${authorizationRequest.id}`} className="underline">
          {authorizationRequest.short_id}
        </Link>
      </DataCell>
      <DataCell>
        {
          centsAmount ?
            <DollarAmount value={centsAmount} convertCents /> :
            `${unitAmount} ${
              get(authorizationRequest, 'fee_schedule_program.unit') ?
                pluralize(
                  get(authorizationRequest, 'fee_schedule_program.unit'),
                  unitAmount,
                ) : ''
            }`
        }
      </DataCell>
      <DataCell>
        {get(authorizationRequest, 'person.last_name')},&nbsp;
        {get(authorizationRequest, 'person.first_name')}
      </DataCell>
      <DataCell className="min-w-[150px]">
        <div className="inline-flex items-center flex-nowrap gap-2 whitespace-nowrap">
          <ServiceAuthorizationStatusIndicator
            status={authorizationRequest.state}
            autoApproved={authorizationRequest.auto_approved}
            isUrgent={authorizationRequest.urgent}
          />
        </div>
      </DataCell>
      <DataCell>{get(authorizationRequest, 'fee_schedule_program.name')}</DataCell>
      <DataCell>
        {get(authorizationRequest, 'requester.last_name')},&nbsp;
        {get(authorizationRequest, 'requester.first_name')}
      </DataCell>
      { showAssignee &&
        (
        <DataCell>
          {
            authorizationRequest.assignee?.first_name ?
              `${get(authorizationRequest, 'assignee.last_name')}, ` +
              `${get(authorizationRequest, 'assignee.first_name')}` :
            '--'
          }
        </DataCell>
      )}
      <DataCell>
        {
          showTimeInQueue ?
            moment(lastUpdated).fromNow(true) :
            lastUpdated.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            })
        }
      </DataCell>
    </DataRow>
  );
};

ServiceAuthorizationTableRow.propTypes = {
  authorizationRequest: PropTypes.object.isRequired,
  showTimeInQueue: PropTypes.bool,
};

ServiceAuthorizationTableRow.defaultProps = {
  showTimeInQueue: false,
};

export default (ServiceAuthorizationTableRow);
